/**
 * Iter 68j — Vendor scheduled bulk-edit jobs list.
 *
 * Surfaces inline in the vendor cars tab. Shows pending + done jobs
 * from the last 30 days; pending jobs can be cancelled with one click.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, X, CheckCircle2, AlertCircle, RefreshCw, Loader2, Wand2 } from "lucide-react";

const FIELD_LABELS = {
  mileage_policy: "Mileage policy",
  mileage_limit_km_per_day: "km/day",
  mileage_extra_km_rate: "Extra km rate",
  free_cancellation_hours: "Free cancellation",
  no_credit_card_required: "No credit card",
  pet_friendly: "Pet-friendly",
  smoking_allowed: "Smoking",
  is_active: "Active",
  cleaning_fee: "Cleaning fee",
  late_return_grace_minutes: "Late-return grace",
};

export default function VendorScheduledJobsTile({ onChanged }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/cars/bulk-schedule");
      setJobs(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh every 60s so a "running → done" transition surfaces
  // without the vendor reloading the page.
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (jobId) => {
    if (!window.confirm("Cancel this scheduled change?")) return;
    setCancelling(jobId);
    try {
      await api.delete(`/vendor/cars/bulk-schedule/${jobId}`);
      toast.success("Scheduled change cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(null);
    }
  };

  const pending = useMemo(() => jobs.filter((j) => j.status === "pending"), [jobs]);
  const recent = useMemo(() => jobs.filter((j) => j.status !== "pending").slice(0, 6), [jobs]);

  if (loading && jobs.length === 0) return null;
  if (pending.length === 0 && recent.length === 0) return null;

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 mt-3" data-testid="vendor-scheduled-jobs">
      <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-700" />
          <h3 className="font-display text-sm font-semibold text-amber-900">
            Scheduled fleet changes
            {pending.length > 0 && (
              <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-amber-800 bg-amber-200 px-1.5 py-0.5 rounded-full">
                {pending.length} pending
              </span>
            )}
          </h3>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="h-7 text-xs text-amber-800 hover:bg-amber-100"
          data-testid="vendor-scheduled-jobs-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </header>

      <ul className="space-y-1.5">
        {[...pending, ...recent].map((j) => (
          <li
            key={j.id}
            className={`flex items-center gap-3 px-3 py-2 rounded-xl bg-white border ${j.status === "pending" ? "border-amber-200" : j.status === "done" ? "border-emerald-100" : j.status === "failed" ? "border-rose-200" : "border-neutral-100"}`}
            data-testid={`vendor-scheduled-job-${j.id}`}
          >
            <span className="shrink-0">
              {j.status === "pending" && <Clock className="h-4 w-4 text-amber-600" />}
              {j.status === "running" && <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />}
              {j.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              {j.status === "failed" && <AlertCircle className="h-4 w-4 text-rose-600" />}
              {j.status === "cancelled" && <X className="h-4 w-4 text-neutral-400" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-neutral-900 truncate">
                {j.label || <span className="italic text-neutral-500">Unlabeled change</span>}
                {" · "}
                <span className="text-neutral-500 text-[12px] font-normal">
                  {Object.keys(j.changes || {}).map((k) => FIELD_LABELS[k] || k).join(", ")}
                </span>
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5">
                {j.status === "pending" && (
                  <>Runs at <strong className="text-amber-700">{new Date(j.run_at).toLocaleString()}</strong></>
                )}
                {j.status === "done" && j.result && (
                  <>Ran at {new Date(j.completed_at || j.run_at).toLocaleString()} — <strong>{j.result.updated}/{j.result.matched} cars</strong> updated</>
                )}
                {j.status === "running" && <>Running now…</>}
                {j.status === "failed" && <>Failed at {new Date(j.failed_at || j.run_at).toLocaleString()} — {(j.error || "").slice(0, 90)}</>}
                {j.status === "cancelled" && <>Cancelled at {new Date(j.cancelled_at || j.run_at).toLocaleString()}</>}
              </div>
            </div>
            {j.status === "pending" && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => cancel(j.id)}
                disabled={cancelling === j.id}
                className="h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                data-testid={`vendor-scheduled-job-cancel-${j.id}`}
              >
                {cancelling === j.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Cancel"}
              </Button>
            )}
          </li>
        ))}
      </ul>

      <p className="text-[11px] text-amber-900/70 mt-2 inline-flex items-center gap-1">
        <Wand2 className="h-3 w-3" /> Scheduled changes run automatically — no need to keep the dashboard open.
      </p>
    </section>
  );
}
